import { useQueryClient } from "@tanstack/react-query";
import { useCommonItems } from "../../../../../../contexts/CommonItensProvider";
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    FormControl,
    Step,
    StepButton,
    StepLabel,
    Stepper,
    Tooltip,
    Typography,
    useMediaQuery
} from "@mui/material";

import { useAppContext } from "../../../../../../contexts/AppContext";
import { useEffect, useState } from "react";
import { getDefaultValueSelectField } from "../../../../../../utils";
import { useForm, useWatch } from "react-hook-form";
import { carregaLockers, manipulaLocker } from "../../configs/functions";
import { fillInputErrors } from "../../../../../../configs/functions";

import { FirstStepLockerForm } from "./FirstStep";
import { SecondStepLockerForm } from "./SecondStep";
import { ThirdStepLockerForm } from "./ThirdStep";

import { CopyAll } from "@mui/icons-material";
import { CopyEquipmentSettingsDialog } from "../CopyEquipmentSettingsDialog";

import {
    buildPortasPayload,
    normalizePortasFromBackend
} from "./config/functions";

export function LockerForm({ open, setOpen, armario, empresas, queryKey }) {
    const { exibirDialog, handleCloseDialog, exibirAlerta } = useCommonItems();

    const { dadosUsuario } = useAppContext();

    const queryClient = useQueryClient();

    const isMobile = useMediaQuery(theme => theme.breakpoints.down("sm"));

    const isEditAction = !!armario;

    const [activeStep, setActiveStep] = useState(0);

    const [qtdColunas, setQtdColunas] = useState(
        armario?.numeroColunas ?? armario?.numero_colunas ?? 1
    );

    const [qtdLinhas, setQtdLinhas] = useState(
        armario?.numeroLinhas ?? armario?.numero_linhas ?? 1
    );

    const [qtdPortas, setQtdPortas] = useState(0);

    const createArrayPortas = total =>
        Array.from({ length: total }, (_, index) => ({
            id: "",
            id_armario: armario ? armario.id : "",
            id_produto: null,
            id_produto_pai: null,
            mustSelectChild: false,
            numero_logico: index + 1,
            physIdx: index,
            status: 'livre',
            doorStatus: null,
            groupId: null,
            isPrimary: false,
            multiplicador: 1,
            portas_fisicas: [index + 1],
            qtd_produto: 0,
            is_dirty: false,
        }));

    const [portas, setPortas] = useState(createArrayPortas(qtdPortas));

    const [isLoading, setIsLoading] = useState(false);

    const {
        register,
        handleSubmit,
        getValues,
        setValue,
        control,
        formState: { dirtyFields, errors },
        trigger,
        setError,
        clearErrors,
        reset,
    } = useForm();

    const [copiarOpen, setCopiarOpen] = useState(false);

    const idEmpresaWatch = useWatch({ control, name: 'id_empresa' });
    const nomeWatch = useWatch({ control, name: 'nome' });
    const tagWatch = useWatch({ control, name: 'tag_identificacao' });
    const pontoEntregaWatch = useWatch({ control, name: 'id_ponto_entrega' });
    const descricaoWatch = useWatch({ control, name: 'descricao' });

    const empresaSelecionada = !!idEmpresaWatch;
    const nomePreenchido = !!nomeWatch?.trim();
    const pontoEntregaPreenchido = !!pontoEntregaWatch;

    const podeCopiar = dadosUsuario.isSempher && activeStep === 0;

    const copiaHabilitada =
        empresaSelecionada &&
        (isEditAction || (nomePreenchido && pontoEntregaPreenchido));

    const tooltipCopia = !empresaSelecionada
        ? 'Selecione uma Empresa antes de copiar as configurações'
        : (!isEditAction && !nomePreenchido)
            ? 'Informe o Nome do armário antes de copiar as configurações'
            : (!isEditAction && !pontoEntregaPreenchido)
                ? 'Selecione o Ponto de Entrega antes de copiar as configurações'
                : 'Copiar as configurações de portas de outro armário ou de uma versão anterior';

    const onSubmit = async data => {
        let formDataToSend = {};

        exibirDialog('Salvando. Aguarde...');

        if (isEditAction) {
            const dirtyFieldKeys = Object.keys(dirtyFields);

            const dirtyData = dirtyFieldKeys.reduce((acc, key) => {
                if (data.hasOwnProperty(key)) {
                    acc[key] = data[key];
                }

                return acc;
            }, {});

            if (
                Object.keys(dirtyData).length === 0 &&
                !portas.some(porta => porta.is_dirty)
            ) {
                exibirAlerta('Atenção', 'Nenhum dado foi alterado.', 'warning');

                setOpen(false);

                handleCloseDialog();

                return;
            }

            formDataToSend = dirtyData;
            formDataToSend.id = armario.id;
        } else {
            delete data.id;

            if (!data.tag_identificacao) {
                delete data.tag_identificacao;
            }

            formDataToSend = data;
        }

        const produtoSemVariacao = portas.find(
            porta => !porta.id_produto && porta.mustSelectChild
        );

        if (produtoSemVariacao) {
            exibirAlerta(
                'Ops',
                'Selecione a variação do produto para a porta ' + produtoSemVariacao.numero_logico,
                'error'
            );

            handleCloseDialog();

            return;
        }

        formDataToSend.portas = buildPortasPayload(portas, qtdColunas);

        try {
            const res = await manipulaLocker(formDataToSend, isEditAction);

            exibirAlerta('Sucesso', res.data.message, 'success');

            setOpen(false);

            queryClient.invalidateQueries({ queryKey });
        } catch (error) {
            let errorMessage = "Erro ao cadastrar Armário.";

            if (error.response) {
                errorMessage = error.response.data.message;

                fillInputErrors(error, setError);
            }

            exibirAlerta('Erro', errorMessage, 'error');
        } finally {
            handleCloseDialog();
        }
    };

    const validaCamposStep01 = async () => {
        const results = await Promise.all([
            trigger('nome'),
            trigger('tag_identificacao'),
            trigger('descricao'),
            trigger('tipo_contrato'),
            trigger('id_empresa'),
            trigger('id_ponto_entrega'),
            trigger('numero_colunas'),
            trigger('numero_linhas'),
        ]);

        return results.every(Boolean);
    };

    const handleNext = async event => {
        event.preventDefault();

        switch (activeStep) {
            case 0:
                if (await validaCamposStep01()) {
                    setActiveStep(prev => prev + 1);
                }

                break;

            default:
                setActiveStep(prev => prev + 1);

                break;
        }
    };

    const handleBack = () => setActiveStep(prev => prev - 1);

    const handleStep = pos => {
        if (pos < activeStep) {
            setActiveStep(pos);
        }
    };

    const steps = [
        'Dados do Armário',
        'Organização do Armário',
        'Configurações dos Armários'
    ];

    const loadConfigsArmario = async () => {
        if (!armario?.id) {
            return;
        }

        try {
            setIsLoading(true);

            const response = await carregaLockers({
                id: armario.id,
                locker_doors: true
            });

            const responseData = response?.data?.data;

            const armarioApi = Array.isArray(responseData)
                ? responseData[0]
                : responseData;

            const portasApi = armarioApi?.portas ?? [];

            const linhas = Number(
                armarioApi?.numeroLinhas ??
                armario?.numeroLinhas ??
                armario?.numero_linhas ??
                1
            );

            const colunas = Number(
                armarioApi?.numeroColunas ??
                armario?.numeroColunas ??
                armario?.numero_colunas ??
                1
            );

            const totalFisicas = linhas * colunas;

            const normalized = normalizePortasFromBackend({
                portasApi,
                totalFisicas,
                cols: colunas,
                idArmario: armario.id,
            });

            setPortas(normalized);
        } catch (error) {
            console.log(error);

            exibirAlerta('Erro ao carregar as Configurações do Armário', '', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        setActiveStep(0);

        if (!open) {
            reset();

            setPortas([]);

            setQtdPortas(0);
            setQtdLinhas(1);
            setQtdColunas(1);

            return;
        }

        const defaultValueIdEmpresa = getDefaultValueSelectField(
            armario,
            armario?.empresa?.id,
            empresas
        );

        const linhas = Number(armario?.numeroLinhas ?? armario?.numero_linhas ?? 1);
        const colunas = Number(armario?.numeroColunas ?? armario?.numero_colunas ?? 1);

        reset({
            id: armario?.id ?? '',
            nome: armario?.nome ?? '',
            tag_identificacao: armario?.tagIdentificacao ?? '',
            status: armario?.status ?? 1,
            tipo_contrato: armario?.tipoContrato ?? '',
            descricao: armario?.descricao ?? '',
            numero_linhas: linhas,
            numero_colunas: colunas,
            numero_portas: armario?.numeroPortas ?? linhas * colunas,
            id_empresa: defaultValueIdEmpresa,
            id_ponto_entrega: armario?.pontoEntrega?.id ?? '',
        });

        setQtdLinhas(linhas);
        setQtdColunas(colunas);
        setQtdPortas(linhas * colunas);

        if (armario?.id) {
            loadConfigsArmario();
        } else {
            setPortas(createArrayPortas(linhas * colunas));
        }
    }, [open, armario, empresas, reset]);

    useEffect(() => {
        if (open && !isEditAction) {
            setPortas(createArrayPortas(qtdPortas));
        }
    }, [open, qtdPortas, isEditAction]);

    return (
        <Dialog
            open={open}
            fullWidth
            maxWidth="lg"
            disableEscapeKeyDown
        >
            <form onSubmit={handleSubmit(onSubmit)}>
                <DialogTitle sx={{ pb: 1 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            gap: 2,
                            flexWrap: { xs: 'wrap', sm: 'nowrap' },
                        }}
                    >
                        <Typography variant="h6" component="div">
                            {dadosUsuario.isSempher
                                ? isEditAction
                                    ? (
                                        <>
                                            Editando Armário{' '}
                                            <strong>{armario.nome}</strong>
                                        </>
                                    )
                                    : 'Cadastrar novo Armário'
                                : (
                                    <>
                                        Visualizando dados de{' '}
                                        <strong>{armario ? armario.nome : ''}</strong>
                                    </>
                                )
                            }
                        </Typography>

                        {podeCopiar && (
                            <Tooltip title={tooltipCopia} arrow>
                                <span>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        type="button"
                                        startIcon={<CopyAll />}
                                        onClick={() => setCopiarOpen(true)}
                                        disabled={!copiaHabilitada}
                                        sx={{
                                            whiteSpace: 'nowrap',
                                            flexShrink: 0
                                        }}
                                    >
                                        Copiar configurações
                                    </Button>
                                </span>
                            </Tooltip>
                        )}
                    </Box>
                </DialogTitle>

                <DialogContent>
                    <DialogContentText>
                        {dadosUsuario.isSempher
                            ? isEditAction
                                ? 'Preencha corretamente os dados.'
                                : 'Preencha corretamente os dados abaixo para cadastrar um novo Armário.'
                            : ''
                        }
                    </DialogContentText>

                    <Stepper
                        activeStep={activeStep}
                        sx={{
                            px: { xs: 2, sm: 10 },
                            py: 2,
                            overflowX: 'auto',
                            '&::-webkit-scrollbar': { display: 'none' },
                            msOverflowStyle: 'none',
                            scrollbarWidth: 'none',
                        }}
                        orientation={isMobile ? 'vertical' : 'horizontal'}
                    >
                        {steps.map((label, index) => (
                            <Step key={label}>
                                <StepButton
                                    color="inherit"
                                    onClick={() => handleStep(index)}
                                >
                                    <StepLabel>{label}</StepLabel>
                                </StepButton>
                            </Step>
                        ))}
                    </Stepper>

                    <Box sx={{ p: 2, backgroundColor: '#fafafa' }}>
                        <FormControl variant="standard" fullWidth>
                            {activeStep === 0 && (
                                <FirstStepLockerForm
                                    values={getValues()}
                                    armario={armario}
                                    empresas={empresas}
                                    register={register}
                                    errors={errors}
                                    clearErrors={clearErrors}
                                    control={control}
                                    setValue={setValue}
                                    portas={portas}
                                    qtdLinhas={qtdLinhas}
                                    qtdColunas={qtdColunas}
                                    setQtdLinhas={value => {
                                        setQtdLinhas(value);
                                        setQtdPortas(value * qtdColunas);
                                    }}
                                    setQtdColunas={value => {
                                        setQtdColunas(value);
                                        setQtdPortas(qtdLinhas * value);
                                    }}
                                    setQtdPortas={setQtdPortas}
                                    onResetPortas={(novasLinhas, novasColunas) =>
                                        setPortas(createArrayPortas(novasLinhas * novasColunas))
                                    }
                                />
                            )}

                            {activeStep === 1 && (
                                <SecondStepLockerForm
                                    portas={portas}
                                    setPortas={setPortas}
                                    qtdPortas={qtdPortas}
                                    qtdColunas={qtdColunas}
                                />
                            )}

                            {activeStep === 2 && (
                                <ThirdStepLockerForm
                                    values={getValues()}
                                    portas={portas}
                                    setPortas={setPortas}
                                    qtdColunas={qtdColunas}
                                />
                            )}
                        </FormControl>
                    </Box>
                </DialogContent>

                <DialogActions>
                    <Button
                        onClick={() => setOpen(false)}
                        variant="contained"
                    >
                        Cancelar
                    </Button>

                    <Divider orientation="vertical" flexItem />

                    {activeStep !== 0 && (
                        <Button
                            onClick={handleBack}
                            variant="outlined"
                            type="button"
                        >
                            Voltar
                        </Button>
                    )}

                    {activeStep !== steps.length - 1
                        ? (
                            <Button
                                onClick={handleNext}
                                variant="contained"
                                type="button"
                                disabled={isLoading}
                            >
                                {!isLoading
                                    ? 'Próximo'
                                    : (
                                        <>
                                            <CircularProgress size={16} sx={{ mr: 1 }} />
                                            Carregando
                                        </>
                                    )
                                }
                            </Button>
                        )
                        : (
                            <Button type="submit" variant="contained">
                                Pronto
                            </Button>
                        )
                    }
                </DialogActions>
            </form>

            <CopyEquipmentSettingsDialog
                open={copiarOpen}
                onClose={() => setCopiarOpen(false)}
                tipo="armario"
                idEmpresa={idEmpresaWatch}
                equipamento={armario}
                dadosCriacao={{
                    nome: nomeWatch,
                    tag_identificacao: tagWatch,
                    id_ponto_entrega: pontoEntregaWatch,
                    descricao: descricaoWatch,
                }}
                onSuccess={() => {
                    queryClient.invalidateQueries({ queryKey });
                    setOpen(false);
                }}
            />
        </Dialog>
    );
}